import { Fragment, Suspense, lazy, useEffect } from "react"
import { Route } from "react-router-dom"
import { ConnectedRouter } from "connected-react-router"
import { Security, LoginCallback, SecureRoute } from "@okta/okta-react"
import { connect } from "react-redux"

import { OktaAuth } from "@okta/okta-auth-js"
import { createBrowserHistory } from "history"
import {
    ThemeProvider,
    StyledEngineProvider,
    createTheme,
} from "@mui/material/styles"
import "ag-grid-community/styles/ag-grid.css"
import "ag-grid-community/styles/ag-theme-material.css"
import config from "./constants/auth-config"
import {
    loadChatBot,
    unLoadChatBot,
    checkRailRoad,
    loadFullStoryAnalytics,
} from "./constants/utils"

import { RAIL_ROADS, URLS } from "./constants/values"
import { PERMISSIONS } from "./constants/permissions"

import Layout from "./components/Layout/Layout"
import IdleTimeCalculator from "./components/IdleTimeCalculator"
import ErrorPage from "./components/ErrorPage"
import Spinner from "./components/Spinner/spinner"

import "./App.scss"

const {
    VITE_APP_DEV_PUBLIC_URL,
    VITE_APP_USE_FULLSTORY_ANALYTICS,
    VITE_APP_PRODUCT_HUB,
    VITE_APP_ENHANCED_VISIBILITY,
} = import.meta.env

const history = createBrowserHistory()
const DashboardLazy = lazy(() => import("./routes/Dashboard"))
const PlanningLazy = lazy(() => import("./routes/Planning"))
const ReviewPlanLazy = lazy(() => import("./routes/ReviewPlan"))
const ManagePlanLazy = lazy(() => import("./routes/ManagePlan"))
const CalendarViewLazy = lazy(() => import("./routes/CalendarView"))
const DiscardLazy = lazy(() => import("./routes/ReviewPlan/DiscardPlan"))
const CarOrderLazy = lazy(() => import("./routes/CarOrder"))
const CreateCarOrderLazy = lazy(
    () => import("./routes/CarOrder/NewCarOrder/CreateNewCarOrder")
)
const SelectPatternLazy = lazy(
    () => import("./routes/CarOrder/NewCarOrder/SelectPattern")
)
const ChargeManagementLazy = lazy(() => import("./routes/ChargeManagement"))
const ChargeManagementDetailsLazy = lazy(
    () => import("./routes/ChargeManagement/ChargeDetails")
)
const NewRequestsLazy = lazy(
    () => import("./routes/GeneralPlacements/NewRequest")
)
const GeneralPlacementLazy = lazy(() => import("./routes/GeneralPlacements"))
const VisibilityLazy = lazy(() => import("./routes/Visibility"))
const EmailNotificationsLazy = lazy(() => import("./routes/EmailNotifications"))
const SwitchRequestLazy = lazy(() => import("./routes/SwitchRequest"))
const NewSwitchRequestLazy = lazy(
    () => import("./routes/SwitchRequest/NewSwitchRequest")
)

const oktaAuth = new OktaAuth({ ...config() })
const theme = createTheme()

const App = ({ userPermissions }) => {
    const restoreOriginalUri = async () => {
        window.location.href = `${VITE_APP_DEV_PUBLIC_URL}/`
    }

    const redirectToDefaultModule = () => {
        return DashboardLazy
    }

    const customAuthHandler = async () => {
        oktaAuth.session.exists().then(function (exists) {
            if (exists) {
                oktaAuth.signInWithRedirect()
            } else {
                window.open(VITE_APP_PRODUCT_HUB, "_self")
            }
        })
    }

    const signInWithRedirect = async () => {
        await oktaAuth.signInWithRedirect()
        redirectToDefaultModule()
    }

    const CALLBACK_PATH = "/callback"
    if (checkRailRoad([...RAIL_ROADS])) loadChatBot()
    else unLoadChatBot()

    if (VITE_APP_USE_FULLSTORY_ANALYTICS === "true") {
        loadFullStoryAnalytics()
    }

    useEffect(() => {
        if (
            userPermissions &&
            userPermissions.permissions &&
            Array.isArray(userPermissions.permissions) &&
            userPermissions.permissions.length === 1 &&
            userPermissions.permissions[0].permissionName ===
                PERMISSIONS.CAN_ACCESS_ENHANCED_VISIBILITY_MODULE
        ) {
            window.location.href = VITE_APP_ENHANCED_VISIBILITY
        }
    }, [userPermissions])

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <Fragment>
                    <ConnectedRouter history={history}>
                        <Security
                            oktaAuth={oktaAuth}
                            restoreOriginalUri={restoreOriginalUri}
                            onAuthRequired={customAuthHandler}
                        >
                            <IdleTimeCalculator />
                            <Layout>
                                <Suspense fallback={<Spinner open={true} />}>
                                    <SecureRoute
                                        exact={true}
                                        path={URLS.DASHBOARD}
                                        component={DashboardLazy}
                                    />
                                    <Route
                                        exact
                                        path={URLS.LAUNCH}
                                        component={() => signInWithRedirect()}
                                    />
                                    <SecureRoute
                                        exact={true}
                                        path={URLS.EMAIL_NOTIFICATIONS}
                                        component={EmailNotificationsLazy}
                                    />

                                    <Route
                                        path={CALLBACK_PATH}
                                        component={LoginCallback}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.PLANNING}
                                        component={PlanningLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.REVIEW_PLAN}
                                        component={ReviewPlanLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.MANAGEMENT}
                                        component={ManagePlanLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.CALENDAR_VIEW}
                                        component={CalendarViewLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.DISCARD}
                                        component={DiscardLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.CAR_ORDER}
                                        component={CarOrderLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.CAR_ORDER_NEW}
                                        component={SelectPatternLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.CREATE_CAR_ORDER}
                                        component={CreateCarOrderLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.INVENTORY}
                                        component={VisibilityLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.VISIBILITY_LAZY}
                                        component={VisibilityLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.CHARGE_MANAGEMENT}
                                        component={ChargeManagementLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.CHARGE_MANAGEMENT_DETAILS}
                                        component={ChargeManagementDetailsLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.GENERAL_PLACEMENT}
                                        component={GeneralPlacementLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={
                                            URLS.GENERAL_PLACEMENT_NEW_REQUEST
                                        }
                                        component={NewRequestsLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.SWITCH_REQUEST_LAZY}
                                        component={SwitchRequestLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.SWITCH_REQUEST_NEW_REQUEST}
                                        component={NewSwitchRequestLazy}
                                    />
                                    <SecureRoute
                                        exact
                                        path={URLS.ERROR}
                                        component={ErrorPage}
                                    />
                                </Suspense>
                            </Layout>
                        </Security>
                    </ConnectedRouter>
                </Fragment>
            </ThemeProvider>
        </StyledEngineProvider>
    )
}

function mapStateToProps(state) {
    return {
        userPermissions: state.userProfile.accesRights,
    }
}

const mapDispatchToProps = {}
export default connect(mapStateToProps, mapDispatchToProps)(App)
